import { useAsyncRead } from '@/hooks/useAsyncRead';
import { NetworkLinks } from '@/features/networks/NetworkLinks';
import { PinnedMixes } from '@/features/networks/NetworkEntry';
import { LoadState } from '@/ui/LoadState';
// features/home/HomePage.tsx — Home feed page.
//
// Assembles HomeHero, FeedTabs, PostCard feed, and publishes the
// three right-rail panels (Trending / WhoToFollow / Communities) into the shell.

import { FollowedDiscussions } from '@/features/discussions/FollowedDiscussions';
import { FeedOrderControl } from './FeedOrderControl';
import { orderFeed } from './orderFeed';
import { useMemo, useEffect, useCallback, useRef, useState } from 'react';
import { useForYouStore } from '@/core/stores/forYouStore';
import { useFeedStore, type PagedFeedKey } from '@/core/stores/feedStore';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button, ConsolePanel } from '@/ui';
import { GuestBanner } from '@/components/GuestBanner';
import { HomeHero } from './HomeHero';
import { DiscoveryPromo } from '@/components/panels/DiscoveryPromo';
import { FeedTabs } from './FeedTabs';
import { useRememberedFeedTab, useFeedPosition } from './useFeedMemory';
import { NewPostsPill } from './NewPostsPill';
import { TrendingPanel } from '@/components/panels/TrendingPanel';
import { WhoToFollowPanel } from '@/components/panels/WhoToFollowPanel';
import { CommunitiesPanel } from './panels/CommunitiesPanel';
import { PostCard } from '@/components/PostCard';
import { usePosts, useSession, usePreferences, usePageTitle } from '@/hooks';
import { useRightRail } from '@/app/shell/RightRail/context';
import { RailGroup, RailPanel } from '@/app/shell/RightRail';
import { postsService } from '@/core/services/postsService';

// ── Styled pieces ──────────────────────────────────────────────────────────────

const Feed = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;
const FeedToolbar = styled.div`
  position: sticky;
  /* The desktop scroller has 16px top padding; cover that inset when pinned. */
  top: -16px;
  z-index: 5;
  background: ${({ theme }) => theme.color.surfaceChrome};
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  @media (max-width: 767.98px) {
    top: 56px;
  }
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  > div {
    flex: 1;
    min-width: min(100%, 360px);
  }
`;
const MixToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  &:empty {
    display: none;
  }
`;

const PrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.xs};
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  cursor: pointer;
  text-decoration: none;
  /* Keep the empty-state link secondary to the shell's Create action. */
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  border-color: ${({ theme }) => theme.color.hairSoft};
  transition: background 120ms ease;
`;

/* Left-aligned, like every other block in the reading column. Centring is for
   auth cards and modal confirm rows; an empty feed is a sentence, not a poster. */
const EmptyStateWrap = styled(ConsolePanel)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 24px 20px;
  text-align: left;
`;

const EmptyText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* The end of a paged tab: the "load more" row, or the line that says there is no more. */
const FeedFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px 0 24px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

// ── Inline subcomponents ───────────────────────────────────────────────────────

function EmptyFollowingState() {
  return (
    <EmptyStateWrap>
      <EmptyText>
        Nothing here yet. This tab shows the people you follow, what they spread, and your own
        posts. Follow a few people, or write your first post and it will appear here.
      </EmptyText>
      <PrimaryLink to="/explore">Find people to follow</PrimaryLink>
    </EmptyStateWrap>
  );
}

function EmptyDiscoverState() {
  return (
    <EmptyStateWrap>
      <EmptyText>Nothing new to discover right now.</EmptyText>
      <PrimaryLink to="/explore">Explore TruXSocial</PrimaryLink>
    </EmptyStateWrap>
  );
}

/**
 * The catch-all empty feed.
 *
 * Only Following and Discover had an empty state. For You, Trending, and — most
 * importantly — the signed-out public feed rendered literally nothing: hero,
 * composer, tabs, and then blank page. Both existing flags were also gated on
 * `isAuthenticated`, so a logged-out visitor arriving on a young platform saw the
 * emptiest possible version of it with no explanation, which is exactly the visitor
 * the public feed exists for.
 */
function EmptyFeedState({ signedIn }: { signedIn: boolean }) {
  return (
    <EmptyStateWrap>
      <EmptyText>
        {signedIn
          ? 'No posts here yet. TruXSocial is small and new. Yours would not get lost.'
          : 'No public posts yet. TruXSocial is small and new.'}
      </EmptyText>
      <PrimaryLink to="/explore">Explore TruXSocial</PrimaryLink>
    </EmptyStateWrap>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function HomePage() {
  const { isAuthenticated, currentUser } = useSession();
  const owner = currentUser?.id ?? 'guest';
  const { posts } = usePosts();
  // Chronological-first by default (anti-virality thesis): land on Following,
  // newest first. "For You" / "Trending" are opt-in tabs — but which tab is
  // "home" is the member's call (Settings → Content Preferences).
  const { prefs } = usePreferences();
  const [tab, setTab] = useRememberedFeedTab(
    owner,
    prefs.rememberFeedPosition,
    prefs.defaultFeedTab,
  );
  usePageTitle(null); // "TruXSocial" default — Home carries no per-page title.

  // A guest gets ONE feed: the public recency list (GET /api/posts/, capped
  // server-side). A signed-in member never loads that whole list any more — every
  // tab below asks the server for exactly what it shows, a page at a time.
  const loadGuest = useCallback(async () => {
    if (isAuthenticated) return true;
    await postsService.listFeed();
    return true;
  }, [isAuthenticated]);
  const guestLoad = useAsyncRead(loadGuest, owner, false);

  // Following / Discover / Trending: computed and paged by the server
  // (?feed=<tab>). Following includes the member's own posts and everything the
  // people they follow spread. The ordered id list lives in feedStore so a post
  // published from anywhere in the shell lands at the top of Following.
  const pagedKey: PagedFeedKey | null = isAuthenticated && tab !== 'for-you' ? tab : null;
  const feed = useFeedStore((s) => (pagedKey ? s.feeds[pagedKey] : null));
  const loadTab = useCallback(async () => {
    if (!pagedKey) return null;
    return postsService.listTab(pagedKey, 0);
  }, [pagedKey]);
  const tabLoad = useAsyncRead(loadTab, owner, null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const loadMore = useCallback(async () => {
    if (!pagedKey || loadingMore) return;
    const next = useFeedStore.getState().feeds[pagedKey]?.next;
    if (next == null) return;
    setLoadingMore(true);
    setMoreFailed(false);
    try {
      await postsService.listTab(pagedKey, next);
    } catch {
      setMoreFailed(true);
    } finally {
      setLoadingMore(false);
    }
  }, [pagedKey, loadingMore]);
  // Load the next page as the reader nears the end of the list. The button below
  // stays as the keyboard path and the fallback where observers are unavailable.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const nextOffset = feed?.next ?? null;
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined' || moreFailed) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadMore();
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, moreFailed, nextOffset]);

  // "For You" asks the server for a ranked, topic-filtered feed (?feed=for-you).
  // The fetched id list lives in a shared store (not local state) so
  // postsService.createPost can prepend a freshly-published post's id to it.
  //
  // The effect is explicitly auth-gated so a stray tab === 'for-you' state left
  // over from a just-signed-out session can never fire this request (and,
  // transitively, postsService.markSeen's POST /api/posts/seen/) for an
  // anonymous caller.
  const forYouIds = useForYouStore((s) => s.ids);
  const setForYouIds = useForYouStore((s) => s.setIds);
  const loadForYou = useCallback(async () => {
    if (!isAuthenticated || tab !== 'for-you') return null;
    return postsService.listFeed({ feed: 'for-you' });
  }, [isAuthenticated, tab]);
  const forYouLoad = useAsyncRead(loadForYou, owner, null);
  useEffect(() => {
    if (forYouLoad.data) setForYouIds(forYouLoad.data.map((post) => post.id));
  }, [forYouLoad.data, setForYouIds]);

  // A tab that was opened earlier this session shows what it had while its fresh
  // first page loads, instead of dropping back to skeletons on every tab switch.
  const tabPending = pagedKey !== null && !feed && (tabLoad.loading || tabLoad.error);
  const forYouPending =
    isAuthenticated && tab === 'for-you' && (forYouLoad.loading || forYouLoad.error);
  const guestPending = !isAuthenticated && (guestLoad.loading || guestLoad.error);

  useFeedPosition(
    owner,
    prefs.rememberFeedPosition,
    !tabPending && !forYouPending && !guestPending,
    tab,
  );

  // NewPostsPill's click handler: refetch whichever list the active tab renders
  // from. This is a discrete user-initiated action (not a remount), so it doesn't
  // need the load effects' cancellation guard.
  const refetchActive = useCallback(async () => {
    if (pagedKey) {
      await postsService.listTab(pagedKey, 0);
      return;
    }
    const forYouPosts = await postsService.listFeed({ feed: 'for-you' });
    setForYouIds(forYouPosts.map((p) => p.id));
  }, [pagedKey, setForYouIds]);

  const visible = useMemo(() => {
    // Guests get ONE feed — the default recency-ordered public list.
    if (!isAuthenticated) return posts;
    const ids = tab === 'for-you' ? forYouIds : (feed?.ids ?? null);
    if (!ids) return [];
    // A hidden post, or one by an author the member just muted, has already left
    // postStore — it simply drops out of the tab here.
    const byId = new Map(posts.map((p) => [p.id, p] as const));
    return orderFeed(
      ids.map((id) => byId.get(id)).filter((p): p is NonNullable<typeof p> => Boolean(p)),
      prefs.feedOrder,
    );
  }, [isAuthenticated, tab, posts, feed, forYouIds, prefs.feedOrder]);

  // Publish right-rail panels — memoised so useRightRail gets a stable node
  const rightRail = useMemo(
    () => (
      <RailGroup pageKey="home">
        <RailPanel id="trending" label="Trending">
          <TrendingPanel />
        </RailPanel>
        <RailPanel id="people" label="Who to follow">
          <WhoToFollowPanel />
        </RailPanel>
        <RailPanel id="communities" label="Communities">
          <CommunitiesPanel />
        </RailPanel>
        <RailPanel id="promo" label="Premium">
          <DiscoveryPromo premium />
        </RailPanel>
      </RailGroup>
    ),
    [],
  );
  useRightRail(rightRail);

  const showEmptyFollowing = isAuthenticated && visible.length === 0 && tab === 'following';
  const showEmptyDiscover = isAuthenticated && visible.length === 0 && tab === 'discover';

  return (
    <Feed>
      <HomeHero />
      {!isAuthenticated && (
        <GuestBanner message="You're browsing TruXSocial. Sign in to post, cherish, and follow." />
      )}
      {/* For You / Following / Trending / Discover are signed-in concepts — a
          guest sees the single public feed below with no tab control at all. */}
      {isAuthenticated && <FollowedDiscussions />}
      {isAuthenticated && (
        <FeedToolbar data-testid="feed-toolbar">
          <FeedTabs active={tab} onChange={setTab} />
          <FeedOrderControl compact />
        </FeedToolbar>
      )}
      <MixToolbar>
        <NetworkLinks destination="home" />
        <PinnedMixes />
      </MixToolbar>
      {isAuthenticated && <NewPostsPill onRefetch={refetchActive} />}
      {forYouPending ? (
        <LoadState {...forYouLoad} label="For You feed" cards={3} />
      ) : tabPending ? (
        <LoadState {...tabLoad} label="feed" cards={3} />
      ) : guestPending ? (
        <LoadState {...guestLoad} label="feed" cards={3} />
      ) : showEmptyFollowing ? (
        <EmptyFollowingState />
      ) : showEmptyDiscover ? (
        <EmptyDiscoverState />
      ) : visible.length === 0 ? (
        <EmptyFeedState signedIn={isAuthenticated} />
      ) : (
        <>
          {visible.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              forYouContext={isAuthenticated && tab === 'for-you'}
              spreadBy={pagedKey === 'following' ? feed?.spreadBy[p.id] : undefined}
            />
          ))}
          {pagedKey && feed && (
            <FeedFooter>
              {feed.next != null ? (
                <>
                  <div ref={sentinelRef} aria-hidden="true" />
                  {moreFailed && <span role="alert">Couldn&apos;t load more posts.</span>}
                  <Button variant="ghost" onClick={() => void loadMore()} disabled={loadingMore}>
                    {loadingMore
                      ? 'Loading more posts'
                      : moreFailed
                        ? 'Try again'
                        : 'Load more posts'}
                  </Button>
                </>
              ) : (
                <span>You&apos;re all caught up.</span>
              )}
            </FeedFooter>
          )}
        </>
      )}
    </Feed>
  );
}
